import { WOO_API_KEY, WOO_API_SECRET, WOO_URL } from "../config";

// Vite or CRA env fallbacks
const WC_URL = `${WOO_URL}/wp-json/wc/v3`;
const WC_KEY = WOO_API_KEY;
const WC_SECRET = WOO_API_SECRET;

if (!WC_URL || !WC_KEY || !WC_SECRET) {
  // eslint-disable-next-line no-console
  console.warn(
    "Woo env missing. Check API_BASE_URL, VITE_WC_KEY, VITE_WC_SECRET / CRA equivalents."
  );
}

function authHeaders() {
  return {
    Authorization: "Basic " + btoa(`${WC_KEY}:${WC_SECRET}`),
    "Content-Type": "application/json",
  };
}

export type WooCategory = {
  id: number;
  name: string;
  slug: string;
  parent: number;
  count: number;
};

export type WooProduct = {
  id: number;
  slug: string;
  name: string;
  status: "draft" | "pending" | "private" | "publish";
  type: "simple" | "grouped" | "external" | "variable";
  regular_price: string;
  sale_price: string;
  price: string;
  manage_stock: boolean;
  stock_quantity: number | null;
  stock_status: "instock" | "outofstock" | "onbackorder";
  description: string;
  short_description: string;
  categories: { id: number; name: string }[];
  attributes?: WooProductAttribute[];
  total_sales?: string; // Woo REST returns this as a string
};

export async function wooGetProduct(id: number): Promise<WooProduct> {
  const res = await fetch(`${WC_URL}/products/${id}`, {
    headers: authHeaders(),
  });
  if (!res.ok)
    throw new Error(
      `Load product ${id} failed: ${res.status} ${await res.text()}`
    );
  return res.json();
}

export async function wooUpdateProduct(
  id: number,
  payload: Partial<WooProduct> & {
    // allow simple fields we'll update
    name?: string;
    type?: "simple" | "grouped" | "external" | "variable";
    regular_price?: string;
    sale_price?: string;
    manage_stock?: boolean;
    stock_quantity?: number | null;
    stock_status?: "instock" | "outofstock" | "onbackorder";
    status?: "draft" | "pending" | "private" | "publish";
    description?: string;
    short_description?: string;
    categories?: { id: number }[];
    images?: { src: string; alt?: string; name?: string }[];
  }
): Promise<WooProduct> {
  const res = await fetch(`${WC_URL}/products/${id}`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok)
    throw new Error(
      `Update product ${id} failed: ${res.status} ${await res.text()}`
    );
  return res.json();
}

export async function wooCreateProduct(
  payload: Partial<WooProduct> & {
    name: string;
    type?: "simple" | "grouped" | "external" | "variable";
    regular_price?: string;
    sale_price?: string;
    manage_stock?: boolean;
    stock_quantity?: number | null;
    stock_status?: "instock" | "outofstock" | "onbackorder";
    status?: "draft" | "pending" | "private" | "publish";
    description?: string;
    short_description?: string;
    categories?: { id: number; name?: string }[];
    images?: { src: string; alt?: string; name?: string }[];
  }
): Promise<WooProduct> {
  const res = await fetch(`${WC_URL}/products`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });

  if (!res.ok)
    throw new Error(`Create product failed: ${res.status} ${await res.text()}`);

  return res.json();
}

export async function wooGetAllCategories(): Promise<WooCategory[]> {
  // Pull all pages up to a reasonable cap
  const perPage = 100;
  let page = 1;
  const all: WooCategory[] = [];
  while (true) {
    const res = await fetch(
      `${WC_URL}/products/categories?per_page=${perPage}&page=${page}`,
      {
        headers: authHeaders(),
      }
    );
    if (!res.ok)
      throw new Error(
        `Load categories failed: ${res.status} ${await res.text()}`
      );
    const chunk = (await res.json()) as WooCategory[];
    all.push(...chunk);
    const totalPages = Number(res.headers.get("X-WP-TotalPages") || "1");
    if (page >= totalPages) break;
    page++;
  }
  return all;
}

export type WooProductAttribute = {
  id: number; // taxonomy id (0 for custom attribute)
  name: string; // "Size", "Color", or "pa_color"
  position?: number;
  visible?: boolean;
  variation?: boolean; // must be true for variation attributes
  options?: string[]; // ["Red","Blue"] for custom; taxonomy terms are handled by ids on the server
};

export type WooVariation = {
  id: number;
  permalink?: string;
  sku?: string | null;
  price?: string;
  regular_price?: string;
  sale_price?: string;
  on_sale?: boolean;
  status?: "publish" | "private" | "draft";
  manage_stock?: boolean;
  stock_quantity?: number | null;
  stock_status?: "instock" | "outofstock" | "onbackorder";
  attributes: { id?: number; name?: string; option: string }[];
};

// Get all variations (handles pagination)
export async function wooGetVariations(
  productId: number
): Promise<WooVariation[]> {
  const perPage = 100;
  let page = 1;
  const out: WooVariation[] = [];
  while (true) {
    const res = await fetch(
      `${WC_URL}/products/${productId}/variations?per_page=${perPage}&page=${page}`,
      {
        headers: authHeaders(),
      }
    );
    if (!res.ok)
      throw new Error(
        `Load variations failed: ${res.status} ${await res.text()}`
      );
    const chunk = (await res.json()) as WooVariation[];
    out.push(...chunk);
    const totalPages = Number(res.headers.get("X-WP-TotalPages") || "1");
    if (page >= totalPages) break;
    page++;
  }
  return out;
}

export async function wooUpdateVariation(
  productId: number,
  variationId: number,
  payload: Partial<WooVariation> & {
    regular_price?: string;
    sale_price?: string;
    manage_stock?: boolean;
    stock_quantity?: number | null;
    stock_status?: "instock" | "outofstock" | "onbackorder";
    sku?: string | null;
    status?: "publish" | "private" | "draft";
    attributes?: { id?: number; name?: string; option: string }[];
  }
): Promise<WooVariation> {
  const res = await fetch(
    `${WC_URL}/products/${productId}/variations/${variationId}`,
    {
      method: "PUT",
      headers: authHeaders(),
      body: JSON.stringify(payload),
    }
  );
  if (!res.ok)
    throw new Error(
      `Update variation failed: ${res.status} ${await res.text()}`
    );
  return res.json();
}

export async function wooCreateVariation(
  productId: number,
  payload: {
    regular_price?: string;
    sale_price?: string;
    manage_stock?: boolean;
    stock_quantity?: number | null;
    stock_status?: "instock" | "outofstock" | "onbackorder";
    sku?: string | null;
    status?: "publish" | "private" | "draft";
    attributes: { id?: number; name?: string; option: string }[];
  }
): Promise<WooVariation> {
  const res = await fetch(`${WC_URL}/products/${productId}/variations`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok)
    throw new Error(
      `Create variation failed: ${res.status} ${await res.text()}`
    );
  return res.json();
}

export async function wooDeleteVariation(
  productId: number,
  variationId: number,
  force = true
): Promise<{ deleted: boolean; id: number }> {
  const res = await fetch(
    `${WC_URL}/products/${productId}/variations/${variationId}?force=${
      force ? "true" : "false"
    }`,
    {
      method: "DELETE",
      headers: authHeaders(),
    }
  );
  if (!res.ok)
    throw new Error(
      `Delete variation failed: ${res.status} ${await res.text()}`
    );
  return res.json();
}

// Product attributes management
export async function wooUpdateProductAttributes(
  productId: number,
  attributes: WooProductAttribute[]
): Promise<WooProduct> {
  const res = await fetch(`${WC_URL}/products/${productId}`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify({ attributes }),
  });
  if (!res.ok)
    throw new Error(
      `Update product attributes failed: ${res.status} ${await res.text()}`
    );
  return res.json();
}

// Order types and structures
export type WooOrderStatus =
  | "pending"
  | "processing"
  | "on-hold"
  | "completed"
  | "cancelled"
  | "refunded"
  | "failed"
  | "trash";

export type WooOrderLineItem = {
  id: number;
  name: string;
  product_id: number;
  variation_id: number;
  quantity: number;
  tax_class: string;
  subtotal: string;
  subtotal_tax: string;
  total: string;
  total_tax: string;
  taxes: any[];
  meta_data: any[];
  sku: string;
  price: number;
  image?: {
    id: number;
    src: string;
  };
};

export type WooShippingLine = {
  id: number;
  method_title: string;
  method_id: string;
  instance_id: string;
  total: string;
  total_tax: string;
  taxes: any[];
};

export type WooTaxLine = {
  id: number;
  rate_code: string;
  rate_id: number;
  label: string;
  compound: boolean;
  tax_total: string;
  shipping_tax_total: string;
};

export type WooBilling = {
  first_name: string;
  last_name: string;
  company: string;
  address_1: string;
  address_2: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  email: string;
  phone: string;
};

export type WooShipping = {
  first_name: string;
  last_name: string;
  company: string;
  address_1: string;
  address_2: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  phone: string;
};

export type WooOrder = {
  id: number;
  parent_id: number;
  status: WooOrderStatus;
  currency: string;
  version: string;
  prices_include_tax: boolean;
  date_created: string;
  date_modified: string;
  discount_total: string;
  discount_tax: string;
  shipping_total: string;
  shipping_tax: string;
  cart_tax: string;
  total: string;
  total_tax: string;
  customer_id: number;
  order_key: string;
  billing: WooBilling;
  shipping: WooShipping;
  payment_method: string;
  payment_method_title: string;
  transaction_id: string;
  customer_ip_address: string;
  customer_user_agent: string;
  created_via: string;
  customer_note: string;
  date_completed: string | null;
  date_paid: string | null;
  cart_hash: string;
  number: string;
  line_items: WooOrderLineItem[];
  tax_lines: WooTaxLine[];
  shipping_lines: WooShippingLine[];
  fee_lines: any[];
  coupon_lines: any[];
  refunds: any[];
};

// Orders API functions (real)
export async function wooGetOrders(
  page = 1,
  perPage = 20,
  status = "",
  search = ""
): Promise<{ orders: WooOrder[]; totalPages: number; total: number }> {
  const params = new URLSearchParams({
    per_page: perPage.toString(),
    page: page.toString(),
  });

  if (status) params.append("status", status);
  if (search) params.append("search", search);

  const res = await fetch(`${WC_URL}/orders?${params.toString()}`, {
    headers: authHeaders(),
  });

  if (!res.ok)
    throw new Error(`Load orders failed: ${res.status} ${await res.text()}`);

  const orders = (await res.json()) as WooOrder[];
  const totalPages = Number(res.headers.get("X-WP-TotalPages") || "1");
  const total = Number(res.headers.get("X-WP-Total") || "0");

  return { orders, totalPages, total };
}

export async function wooGetOrder(
  idOrNumber: number | string
): Promise<WooOrder> {
  // Helper to try direct GET by internal numeric ID
  const tryGetById = async (numericId: number): Promise<WooOrder | null> => {
    const res = await fetch(`${WC_URL}/orders/${numericId}`, {
      headers: authHeaders(),
    });
    if (res.ok) return res.json();
    // 404/400/403 will be handled by fallback search
    return null;
  };

  // Normalize input
  const asString = String(idOrNumber).trim();
  const digitsOnly = (s: string) => s.replace(/\D+/g, "");
  const isAllDigits = /^\d+$/.test(asString);

  // 1) If numeric-like, first attempt direct fetch by internal ID
  if (isAllDigits) {
    const numericId = Number(asString);
    const byId = await tryGetById(numericId);
    if (byId) return byId;
  }

  // 2) Fallback: search by order number (supports sequential order number plugins)
  // We'll search a few pages to find an exact number match, ignoring non-digits and optional leading '#'
  const wantedDigits = digitsOnly(asString.replace(/^#/, ""));
  if (!wantedDigits) {
    throw new Error(`Load order failed: invalid identifier '${asString}'`);
  }

  const perPage = 50;
  let page = 1;

  // Prime the request to learn total pages
  let initial = await fetch(
    `${WC_URL}/orders?per_page=${perPage}&page=${page}&search=${encodeURIComponent(
      wantedDigits
    )}`,
    { headers: authHeaders() }
  );
  if (!initial.ok) {
    throw new Error(
      `Search orders failed: ${initial.status} ${await initial.text()}`
    );
  }
  let list: WooOrder[] = await initial.json();
  const totalPages = Number(initial.headers.get("X-WP-TotalPages") || "1");

  const matchIn = (orders: WooOrder[]) =>
    orders.find((o) => digitsOnly(o.number || String(o.id)) === wantedDigits);

  let found = matchIn(list);
  while (!found && page < totalPages) {
    page += 1;
    const res = await fetch(
      `${WC_URL}/orders?per_page=${perPage}&page=${page}&search=${encodeURIComponent(
        wantedDigits
      )}`,
      { headers: authHeaders() }
    );
    if (!res.ok) break;
    const chunk: WooOrder[] = await res.json();
    found = matchIn(chunk);
  }

  if (found) return found;

  throw new Error(
    `Load order failed: not found by id or number ('${asString}')`
  );
}

export async function wooUpdateOrder(
  id: number,
  order: Partial<WooOrder>
): Promise<WooOrder> {
  const res = await fetch(`${WC_URL}/orders/${id}`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(order),
  });

  if (!res.ok)
    throw new Error(
      `Update order ${id} failed: ${res.status} ${await res.text()}`
    );

  return res.json();
}

// ===== Refunds =====
export type WooRefund = {
  id: number;
  date_created: string;
  amount: string;
  reason: string;
  refunded_by?: number;
  line_items: Array<{
    id?: number;
    name?: string;
    product_id?: number;
    variation_id?: number;
    quantity?: number;
    subtotal?: string;
    total?: string;
    tax_class?: string;
    subtotal_tax?: string;
    total_tax?: string;
    taxes?: any[];
    meta_data?: any[];
    sku?: string;
    price?: number;
  }>;
};

export async function wooGetRefunds(orderId: number): Promise<WooRefund[]> {
  const res = await fetch(`${WC_URL}/orders/${orderId}/refunds`, {
    headers: authHeaders(),
  });
  if (!res.ok)
    throw new Error(
      `Load refunds for order ${orderId} failed: ${
        res.status
      } ${await res.text()}`
    );
  return res.json();
}

export async function wooCreateRefund(
  orderId: number,
  payload: {
    amount: string; // required
    reason?: string;
    line_items?: Array<{
      id?: number; // line item ID
      name?: string;
      product_id?: number;
      variation_id?: number;
      quantity?: number;
      refund_total?: string; // negative amount per line
      refund_tax?: Array<{ id: number; total: string }>;
    }>;
    refund_payment?: boolean; // trigger gateway refund if supported
    api_refund?: boolean; // legacy flag some gateways use
    restock_items?: boolean;
  }
): Promise<WooRefund> {
  const res = await fetch(`${WC_URL}/orders/${orderId}/refunds`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok)
    throw new Error(
      `Create refund for order ${orderId} failed: ${
        res.status
      } ${await res.text()}`
    );
  return res.json();
}

export async function wooDeleteRefund(
  orderId: number,
  refundId: number,
  force = true
): Promise<{ deleted: boolean; id: number }> {
  const res = await fetch(
    `${WC_URL}/orders/${orderId}/refunds/${refundId}?force=${
      force ? "true" : "false"
    }`,
    {
      method: "DELETE",
      headers: authHeaders(),
    }
  );
  if (!res.ok)
    throw new Error(
      `Delete refund ${refundId} for order ${orderId} failed: ${
        res.status
      } ${await res.text()}`
    );
  return res.json();
}

// ===== Order Notes =====
export type WooOrderNote = {
  id: number;
  date_created: string;
  note: string;
  customer_note: boolean;
  added_by_user: boolean;
};

export async function wooGetOrderNotes(
  orderId: number
): Promise<WooOrderNote[]> {
  const res = await fetch(`${WC_URL}/orders/${orderId}/notes`, {
    headers: authHeaders(),
  });
  if (!res.ok)
    throw new Error(
      `Load notes for order ${orderId} failed: ${
        res.status
      } ${await res.text()}`
    );
  return res.json();
}

export async function wooCreateOrderNote(
  orderId: number,
  payload: { note: string; customer_note?: boolean }
): Promise<WooOrderNote> {
  const res = await fetch(`${WC_URL}/orders/${orderId}/notes`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok)
    throw new Error(
      `Create note for order ${orderId} failed: ${
        res.status
      } ${await res.text()}`
    );
  return res.json();
}

export async function wooDeleteOrderNote(
  orderId: number,
  noteId: number,
  force = true
): Promise<{ deleted: boolean; id: number }> {
  const res = await fetch(
    `${WC_URL}/orders/${orderId}/notes/${noteId}?force=${
      force ? "true" : "false"
    }`,
    {
      method: "DELETE",
      headers: authHeaders(),
    }
  );
  if (!res.ok)
    throw new Error(
      `Delete note ${noteId} for order ${orderId} failed: ${
        res.status
      } ${await res.text()}`
    );
  return res.json();
}

// ===== Products list (paged) =====
export async function wooGetProducts(
  page = 1,
  perPage = 20,
  status = "",
  search = ""
): Promise<{ products: WooProduct[]; totalPages: number; total: number }> {
  const params = new URLSearchParams({
    per_page: String(perPage),
    page: String(page),
  });
  if (status) params.append("status", status);
  if (search) params.append("search", search);

  const res = await fetch(`${WC_URL}/products?${params.toString()}`, {
    headers: authHeaders(),
  });
  if (!res.ok)
    throw new Error(`Load products failed: ${res.status} ${await res.text()}`);

  const products = (await res.json()) as WooProduct[];
  const totalPages = Number(res.headers.get("X-WP-TotalPages") || "1");
  const total = Number(res.headers.get("X-WP-Total") || "0");
  return { products, totalPages, total };
}

// ===== Orders in date range (auto-pagination) =====
export async function wooGetOrdersInRange(
  afterISO: string,
  beforeISO?: string,
  perPage = 100,
  maxPages = 10,
  status = ""
): Promise<WooOrder[]> {
  const out: WooOrder[] = [];
  let page = 1;
  const paramsBase = new URLSearchParams({
    per_page: String(perPage),
    after: afterISO,
  });
  if (beforeISO) paramsBase.set("before", beforeISO);
  if (status) paramsBase.set("status", status);

  while (page <= maxPages) {
    const params = new URLSearchParams(paramsBase);
    params.set("page", String(page));
    const res = await fetch(`${WC_URL}/orders?${params.toString()}`, {
      headers: authHeaders(),
    });
    if (!res.ok)
      throw new Error(
        `Load orders (range) failed on page ${page}: ${
          res.status
        } ${await res.text()}`
      );
    const chunk = (await res.json()) as WooOrder[];
    out.push(...chunk);

    const totalPages = Number(res.headers.get("X-WP-TotalPages") || "1");
    if (page >= totalPages) break;
    if (chunk.length === 0) break; // safety
    page += 1;
  }
  return out;
}

// Coupon types and API functions
export type WooCoupon = {
  id: number;
  code: string;
  amount: string;
  date_created: string;
  date_modified: string;
  discount_type: "percent" | "fixed_cart" | "fixed_product";
  description: string;
  date_expires: string | null;
  usage_count: number;
  individual_use: boolean;
  product_ids: number[];
  excluded_product_ids: number[];
  usage_limit: number | null;
  usage_limit_per_user: number | null;
  limit_usage_to_x_items: number | null;
  free_shipping: boolean;
  product_categories: number[];
  excluded_product_categories: number[];
  exclude_sale_items: boolean;
  minimum_amount: string;
  maximum_amount: string;
  email_restrictions: string[];
  used_by: string[];
  status: "publish" | "draft" | "pending" | "private";
};

// Get all coupons with pagination
export async function wooGetCoupons(
  page = 1,
  perPage = 20,
  search = "",
  status = ""
): Promise<{ coupons: WooCoupon[]; totalPages: number; total: number }> {
  const params = new URLSearchParams({
    per_page: perPage.toString(),
    page: page.toString(),
  });

  if (search) params.append("search", search);
  if (status) params.append("status", status);

  const res = await fetch(`${WC_URL}/coupons?${params}`, {
    headers: authHeaders(),
  });

  if (!res.ok)
    throw new Error(`Load coupons failed: ${res.status} ${await res.text()}`);

  const coupons = (await res.json()) as WooCoupon[];
  const totalPages = Number(res.headers.get("X-WP-TotalPages") || "1");
  const total = Number(res.headers.get("X-WP-Total") || "0");

  return { coupons, totalPages, total };
}

// Get single coupon
export async function wooGetCoupon(id: number): Promise<WooCoupon> {
  const res = await fetch(`${WC_URL}/coupons/${id}`, {
    headers: authHeaders(),
  });
  if (!res.ok)
    throw new Error(`Load coupon failed: ${res.status} ${await res.text()}`);
  return res.json();
}

// Create new coupon
export async function wooCreateCoupon(
  coupon: Partial<WooCoupon> & {
    code: string;
    discount_type: string;
    amount: string;
  }
): Promise<WooCoupon> {
  const res = await fetch(`${WC_URL}/coupons`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(coupon),
  });
  if (!res.ok)
    throw new Error(`Create coupon failed: ${res.status} ${await res.text()}`);
  return res.json();
}

// Update coupon
export async function wooUpdateCoupon(
  id: number,
  coupon: Partial<WooCoupon>
): Promise<WooCoupon> {
  const res = await fetch(`${WC_URL}/coupons/${id}`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(coupon),
  });
  if (!res.ok)
    throw new Error(`Update coupon failed: ${res.status} ${await res.text()}`);
  return res.json();
}

// Delete coupon
export async function wooDeleteCoupon(
  id: number,
  force = true
): Promise<{ deleted: boolean; id: number }> {
  const res = await fetch(
    `${WC_URL}/coupons/${id}?force=${force ? "true" : "false"}`,
    {
      method: "DELETE",
      headers: authHeaders(),
    }
  );
  if (!res.ok)
    throw new Error(`Delete coupon failed: ${res.status} ${await res.text()}`);
  return res.json();
}
